import {
  createBaseThemeOptions,
  createUnifiedTheme,
  defaultTypography,
  palettes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import { AppTheme } from '@backstage/core-plugin-api';
import { pageCssOverrides } from './pageCssOverrides';
import tokens from './tokens';

/**
 * These theme customizations will be set globally for Backstage and Material-UI.
 *
 * There are a couple of things to keep in mind that will make the theme work best
 * for everyone.
 *
 * Try to use a specific component override before using `MuiCssBaseline`, this
 * makes sure that the styling will only apply to that component and not have
 * any effects on other elements.
 *
 * When doing custom CSS please avoid using `!important`. It will get your CSS
 * working, but doesn't let plugin authors customize the component to work best
 * for their plugin/page. It should be used rarely, and a comment should be
 * left for the reason why it was used.
 *
 * There are some components that have higher specificity because of how
 * Material UI has created their components. If you run into this case where
 * your custom styles in here are being overridden by the normal styles, opt for
 * doubling up the selector instead of using `!important`. You can do this by
 * using `&&` as a key in the theme. There are some examples below.
 *
 * Reference: https://www.w3schools.com/css/css_specificity.asp
 */

const DEFAULT_FONT_FAMILY = 'system-ui, sans-serif';

const BackstageSidebarItem = {
  styleOverrides: {
    secondaryAction: {
      width: `unset`,
    },
  },
};

const BackstageHeaderLabel = {
  styleOverrides: {
    label: {
      color: 'inherit',
    },
    value: {
      color: 'inherit',
    },
  },
};

const BackstageHeader = {
  styleOverrides: {
    header: {
      backgroundImage: 'unset',
      boxShadow: 'unset',
    },
    title: {
      color: 'inherit',
    },
    subtitle: {
      color: 'inherit',
    },
    type: {
      color: 'inherit',
    },
    breadcrumb: {
      color: 'inherit',
    },
  },
};

const MuiCssBaseline = {
  styleOverrides: {
    ...pageCssOverrides,
    'header button#long-menu': {
      color: 'inherit',
    },
  },
};

const runwayComponents = {
  BackstageSidebarItem,
  BackstageHeader,
  BackstageHeaderLabel,
  MuiCssBaseline,
};

export const RunwayDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      mode: 'dark',
      primary: {
        main: tokens.dark.bg.fill.bold.blue,
      },
      secondary: {
        main: tokens.dark.bg.fill.bold.blue,
      },
      background: {
        default: tokens.dark.bg.page.alt,
        paper: tokens.dark.bg.surface.main,
      },
      border: tokens.dark.border.tertiary,
      navigation: {
        background: tokens.dark.bg.fill.main,
        color: tokens.dark.text.secondary,
        indicator: tokens.dark.outline.active,
        selectedColor: tokens.dark.text.interactive,
        submenu: {
          background: tokens.dark.bg.fill.alt,
        },
        navItem: {
          hoverBackground: tokens.dark.bg.fill.hover,
        },
      },
      link: tokens.dark.text.interactive,
      text: {
        primary: tokens.dark.text.primary,
        secondary: tokens.dark.text.secondary,
        disabled: tokens.dark.text.disabled,
      },
      errorText: tokens.dark.text.error,
      infoText: tokens.dark.text.info,
      warningText: tokens.dark.text.warning,
      errorBackground: tokens.dark.bg.fill.error.subtle,
      infoBackground: tokens.dark.bg.fill.info.subtle,
      warningBackground: tokens.dark.bg.fill.warning.subtle,
    },
    typography: {
      ...defaultTypography,
      fontFamily: DEFAULT_FONT_FAMILY,
    },
  }),
  components: runwayComponents,
});

export const RunwayLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      mode: 'light',
      primary: {
        main: tokens.light.bg.fill.bold.blue,
      },
      secondary: {
        main: tokens.light.bg.fill.bold.blue,
      },
      background: {
        default: tokens.light.bg.page.alt,
        paper: tokens.light.bg.surface.main,
      },
      border: tokens.light.border.secondary,
      navigation: {
        background: tokens.light.bg.fill.main,
        color: tokens.light.text.secondary,
        indicator: tokens.light.outline.active,
        selectedColor: tokens.light.text.interactive,
        submenu: {
          background: tokens.light.bg.fill.alt,
        },
        navItem: {
          hoverBackground: tokens.light.bg.fill.hover,
        },
      },
      link: tokens.light.text.interactive,
      text: {
        primary: tokens.light.text.primary,
        secondary: tokens.light.text.secondary,
        disabled: tokens.light.text.disabled,
      },
      errorText: tokens.light.text.error,
      infoText: tokens.light.text.info,
      warningText: tokens.light.text.warning,
      errorBackground: tokens.light.bg.fill.error.subtle,
      infoBackground: tokens.light.bg.fill.info.subtle,
      warningBackground: tokens.light.bg.fill.warning.subtle,
    },
    typography: {
      ...defaultTypography,
      fontFamily: DEFAULT_FONT_FAMILY,
    },
  }),
  components: runwayComponents,
});

export const RunwayThemes: AppTheme[] = [
  {
    id: 'light',
    title: 'Light',
    variant: 'light',
    Provider: (props: any) => <UnifiedThemeProvider theme={RunwayLightTheme}>{props.children}</UnifiedThemeProvider>,
  },
  {
    id: 'dark',
    title: 'Dark',
    variant: 'dark',
    Provider: (props: any) => <UnifiedThemeProvider theme={RunwayDarkTheme}>{props.children}</UnifiedThemeProvider>,
  },
];
